import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  AlertTriangle, Building2, CheckCircle2, ChevronLeft, ChevronRight, ExternalLink,
  FileText, Loader2, Minus, PenLine, Plus, RotateCcw, XCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { validatePersonFullName } from '../lib/personNames';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const MOBILE_QUERY = '(max-width: 640px)';
const MIN_ZOOM = 0.75;
const MAX_ZOOM = 1.5;
const ZOOM_STEP = 0.15;

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_LABELS = { landlord: 'Landlord', tenant: 'Tenant', guarantor: 'Guarantor' };

function ParticipantRow({ p, isSelf }) {
  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${isSelf ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50'}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="font-medium text-gray-800">{p.signer_name}</span>
        <Badge variant="outline" className="text-xs capitalize">{ROLE_LABELS[p.role] || p.role}</Badge>
        {isSelf && <Badge variant="outline" className="text-xs text-blue-700 border-blue-300">You</Badge>}
      </div>
      {p.signed ? (
        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
      ) : (
        <div className="h-4 w-4 rounded-full border-2 border-gray-300 shrink-0" />
      )}
    </div>
  );
}

export function SignDocument() {
  useEffect(() => { document.title = 'Sign Document — SIYAK'; }, []);

  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);      // { participant, document }
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);       // { status, message } after signing

  // Form state
  const [signerName, setSignerName] = useState('');
  const [consented, setConsented] = useState(false);
  const [authorisedCompany, setAuthorisedCompany] = useState(false);

  // PDF viewer state
  const [pdfData, setPdfData] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [documentViewed, setDocumentViewed] = useState(false);
  const markViewedCalledRef = useRef(false);

  // react-pdf rendering state
  const viewportRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [renderError, setRenderError] = useState('');
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(MOBILE_QUERY);
    const update = () => setIsNarrow(mq.matches);
    update();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener(update);
    return () => mq.removeListener(update);
  }, []);

  useEffect(() => {
    const node = viewportRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      setContainerWidth(entries[0]?.contentRect?.width || 0);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [pdfData]); // re-observe when pdf is loaded and panel appears

  // Desktop multi-page: mark viewed when user scrolls to within 120px of the bottom
  useEffect(() => {
    if (isNarrow || !numPages || numPages <= 1 || documentViewed) return undefined;
    const node = viewportRef.current;
    if (!node) return undefined;
    const handleScroll = () => {
      if (node.scrollTop + node.clientHeight >= node.scrollHeight - 120) {
        setDocumentViewed(true);
      }
    };
    node.addEventListener('scroll', handleScroll, { passive: true });
    return () => node.removeEventListener('scroll', handleScroll);
  }, [isNarrow, numPages, documentViewed]);

  // Single-page doc on any device, or mobile: last page reached → mark viewed
  useEffect(() => {
    if (!numPages || documentViewed) return;
    if (numPages === 1 || (isNarrow && pageNumber >= numPages)) {
      setDocumentViewed(true);
    }
  }, [numPages, isNarrow, pageNumber, documentViewed]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/sign/${token}`);
        setInfo(data);
        setSignerName(data.participant?.signer_name || '');
      } catch (err) {
        const status = err?.response?.status;
        if (status === 410) {
          setError(err.response.data?.detail || 'This signing link has expired or been used.');
        } else if (status === 404) {
          setError('Signing link not found.');
        } else {
          setError('Unable to load signing page. Please try again or contact support.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  // Fetch PDF bytes once we know the PDF is available
  useEffect(() => {
    if (!info?.pdf_available || !info?.pdf_url) return;

    const controller = new AbortController();

    const fetchPdf = async () => {
      setPdfLoading(true);
      setPdfError(null);
      setNumPages(null);
      setPageNumber(1);
      setRenderError('');
      try {
        const response = await fetch(`${API_URL}${info.pdf_url}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buf = await response.arrayBuffer();
        setPdfData(new Uint8Array(buf));
      } catch (err) {
        if (err.name === 'AbortError') return;
        setPdfError('Unable to load the document PDF. You can download it instead.');
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdf();

    return () => {
      controller.abort();
    };
  }, [info?.pdf_available, info?.pdf_url]);

  // Fire-and-forget mark-viewed call when documentViewed flips to true
  useEffect(() => {
    if (!documentViewed || markViewedCalledRef.current) return;
    markViewedCalledRef.current = true;
    fetch(`${API_URL}/api/sign/${token}/mark-viewed`, { method: 'POST' }).catch(() => {});
  }, [documentViewed, token]);

  const isCompany = info?.participant?.entity_type === 'company';
  const alreadySigned = info?.participant?.signed;
  const pdfUrl = info?.pdf_url ? `${API_URL}${info.pdf_url}` : null;
  const documentAccessible = Boolean(info?.pdf_available && pdfUrl);

  const documentFile = useMemo(() => (pdfData ? { data: pdfData } : null), [pdfData]);

  const pageWidth = useMemo(() => {
    const width = containerWidth || (isNarrow ? 360 : 720);
    const fitted = Math.max(260, Math.min(width - 16, isNarrow ? 620 : 720));
    return Math.round(fitted * zoom);
  }, [containerWidth, isNarrow, zoom]);

  const visiblePages = useMemo(() => {
    if (!numPages) return [];
    if (isNarrow) return [Math.min(pageNumber, numPages)];
    return Array.from({ length: numPages }, (_, i) => i + 1);
  }, [isNarrow, numPages, pageNumber]);

  const zoomPercent = Math.round(zoom * 100);
  const canNavigate = isNarrow && Number(numPages) > 1;

  const handleSign = async () => {
    const fullName = validatePersonFullName(signerName, 'Signer Full Name');
    if (!fullName.valid) { toast.error(fullName.error); return; }
    if (!documentAccessible) { toast.error('The document is not available to review yet. Please contact your landlord.'); return; }
    if (!documentViewed) { toast.error('Please scroll through and review the document above before signing.'); return; }
    if (!consented) { toast.error('Please confirm your consent to sign'); return; }
    if (isCompany && !authorisedCompany) { toast.error('Please confirm you are authorised to sign on behalf of the company'); return; }

    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/sign/${token}`, {
        signer_name: fullName.value,
        consented,
        authorised_company: isCompany ? authorisedCompany : null,
        signer_role: info?.participant?.signer_role || null,
        entity_name: info?.participant?.entity_name || null,
      });
      setDone(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit signature. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Layout wrapper ────────────────────────────────────────────────────────
  const wrap = (children) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 flex flex-col">
      <header className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <a
              href="https://www.siyak.co.uk"
              aria-label="SIYAK home"
              className="inline-flex items-center"
            >
              <img
                src="/brand/logo-header.png"
                alt="SIYAK"
                className="h-8 w-auto sm:h-9"
              />
            </a>
            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-[#1e3a5f]">Document signing</p>
              <p className="text-xs text-muted-foreground">Review and sign your tenancy document securely</p>
            </div>
          </div>
          <Badge variant="outline" className="border-[#1e3a5f]/20 text-[#1e3a5f]">
            Secure signing
          </Badge>
        </div>
      </header>
      <main className="flex-1 px-4 py-8 sm:px-6 sm:py-10">
        <div className="mx-auto w-full max-w-2xl">
          <div className="mb-6 rounded-2xl border border-[#1e3a5f]/10 bg-white/80 p-4 shadow-sm sm:p-5">
            <p className="text-sm font-medium text-[#1e3a5f]">Signature request</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Check the document details, confirm your identity, and apply your signature.
            </p>
          </div>
          {children}
        </div>
      </main>
      <footer className="border-t bg-white/90 px-4 py-6 text-center text-xs text-muted-foreground sm:px-6">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-center gap-2 sm:flex-row sm:gap-3">
          <a
            href="https://www.siyak.co.uk"
            aria-label="SIYAK home"
            className="inline-flex items-center"
          >
            <img
              src="/brand/logo-header.png"
              alt="SIYAK"
              className="h-5 w-auto opacity-80"
            />
          </a>
          <span>SIYAK Real Estate Ltd · Registered in England &amp; Wales · ICO Registered: ZC111010</span>
        </div>
      </footer>
    </div>
  );

  if (loading) {
    return wrap(
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  if (error) {
    return wrap(
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center space-y-3">
        <XCircle className="h-10 w-10 text-red-500 mx-auto" />
        <h1 className="font-heading font-bold text-xl text-gray-800">Link Unavailable</h1>
        <p className="text-sm text-muted-foreground">{error}</p>
        <p className="text-xs text-muted-foreground">If you believe this is an error, please contact your landlord.</p>
      </div>
    );
  }

  if (done) {
    const isFullySigned = done.status === 'fully_signed';
    const signerHasAccount = Boolean(info?.participant?.has_account);
    return wrap(
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center space-y-4">
        <CheckCircle2 className={`h-12 w-12 mx-auto ${isFullySigned ? 'text-green-500' : 'text-blue-500'}`} />
        <h1 className="font-heading font-bold text-xl text-gray-800">
          {isFullySigned ? 'Document Fully Executed' : 'Signature Received'}
        </h1>
        <p className="text-sm text-muted-foreground">{done.message}</p>
        {isFullySigned && (
          <p className="text-xs text-muted-foreground">
            {signerHasAccount
              ? 'Your signed document will be available in your dashboard.'
              : 'You will receive a copy by email.'}
          </p>
        )}
      </div>
    );
  }

  const { participant, document: doc } = info;

  if (alreadySigned) {
    return wrap(
      <div className="bg-white rounded-xl border shadow-sm p-8 text-center space-y-3">
        <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
        <h1 className="font-heading font-bold text-xl text-gray-800">Already Signed</h1>
        <p className="text-sm text-muted-foreground">You have already signed this document.</p>
      </div>
    );
  }

  return wrap(
    <div className="space-y-5">
      {/* Document card */}
      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-4">
        <div className="flex items-start gap-3">
          <FileText className="h-6 w-6 text-[#1e3a5f] shrink-0 mt-0.5" />
          <div>
            <h1 className="font-heading font-bold text-xl text-[#1e3a5f]">{doc.title}</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{doc.property_address}</p>
          </div>
        </div>

        {/* Participant status list */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Signatories</p>
          {doc.participants.map((p, i) => (
            <ParticipantRow key={i} p={p} isSelf={i === participant.index} />
          ))}
        </div>

        {/* Inline PDF viewer */}
        {documentAccessible && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Document</p>
            {pdfLoading && (
              <div className="flex items-center justify-center w-full min-h-[400px] rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin" />
                  <span className="text-sm">Loading document…</span>
                </div>
              </div>
            )}
            {pdfError && !pdfLoading && (
              <div className="flex flex-col items-center gap-3 w-full min-h-[120px] rounded-lg border border-amber-200 bg-amber-50 p-5 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <p className="text-sm text-amber-800">{pdfError}</p>
                <Button asChild variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100">
                  <a href={pdfUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Download PDF
                  </a>
                </Button>
              </div>
            )}
            {renderError && !pdfLoading && !pdfError && (
              <div className="flex flex-col items-center gap-3 w-full min-h-[120px] rounded-lg border border-amber-200 bg-amber-50 p-5 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <p className="text-sm text-amber-800">{renderError}</p>
                <Button asChild variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100">
                  <a href={pdfUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Download PDF
                  </a>
                </Button>
              </div>
            )}
            {documentFile && !pdfLoading && !pdfError && !renderError && (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                {/* Controls bar */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-white px-3 py-2 text-sm">
                  <div className="flex min-h-9 items-center gap-2">
                    {canNavigate ? (
                      <>
                        <Button type="button" variant="outline" size="sm" className="h-9 min-w-9 px-2"
                          disabled={pageNumber <= 1}
                          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                          aria-label="Previous page">
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="min-w-20 text-center text-xs font-medium text-slate-700">
                          Page {Math.min(pageNumber, numPages)} of {numPages}
                        </span>
                        <Button type="button" variant="outline" size="sm" className="h-9 min-w-9 px-2"
                          disabled={pageNumber >= numPages}
                          onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
                          aria-label="Next page">
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs font-medium text-slate-700">
                        {numPages ? `${numPages} ${numPages === 1 ? 'page' : 'pages'}` : 'Preparing pages…'}
                      </span>
                    )}
                  </div>
                  <div className="flex min-h-9 items-center gap-2">
                    <Button type="button" variant="outline" size="sm" className="h-9 min-w-9 px-2"
                      disabled={zoom <= MIN_ZOOM}
                      onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Number((z - ZOOM_STEP).toFixed(2))))}
                      aria-label="Zoom out">
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-9 min-w-14 px-2 gap-1"
                      disabled={zoom === 1}
                      onClick={() => setZoom(1)}
                      aria-label="Reset zoom">
                      <RotateCcw className="h-3.5 w-3.5" />
                      <span className="text-xs">{zoomPercent}%</span>
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-9 min-w-9 px-2"
                      disabled={zoom >= MAX_ZOOM}
                      onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Number((z + ZOOM_STEP).toFixed(2))))}
                      aria-label="Zoom in">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {/* PDF canvas */}
                <div ref={viewportRef} className="max-h-[65vh] overflow-auto bg-slate-100 px-2 py-3 sm:px-4">
                  <Document
                    file={documentFile}
                    loading={
                      <div className="flex min-h-[320px] items-center justify-center gap-2 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span className="text-sm">Rendering document…</span>
                      </div>
                    }
                    error=""
                    onSourceError={(err) => setRenderError(`PDF source failed: ${err?.message || err}`)}
                    onLoadSuccess={({ numPages: n }) => {
                      setNumPages(n);
                      setPageNumber(1);
                      setRenderError('');
                    }}
                    onLoadError={(err) => setRenderError(`PDF parse failed: ${err?.message || err}`)}
                  >
                    <div className="mx-auto flex w-max max-w-none flex-col items-center gap-4">
                      {visiblePages.map((page) => (
                        <Page
                          key={`sign-doc-${page}-${pageWidth}`}
                          pageNumber={page}
                          width={pageWidth}
                          renderAnnotationLayer
                          renderTextLayer
                          onRenderError={(err) => setRenderError(`Page ${page} failed: ${err?.message || err}`)}
                          loading={
                            <div className="flex h-72 w-64 items-center justify-center rounded border bg-white text-xs text-muted-foreground">
                              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Page {page}
                            </div>
                          }
                        />
                      ))}
                    </div>
                  </Document>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {documentViewed
                ? <span className="text-green-700 flex items-center gap-1"><CheckCircle2 className="h-3 w-3 inline" /> Document reviewed</span>
                : 'Please review the document above before signing'}
            </p>
          </div>
        )}

        {!documentAccessible && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">Document PDF is unavailable. Contact your landlord before signing.</p>
          </div>
        )}
      </div>

      {/* Signing form */}
      <div className="bg-white rounded-xl border shadow-sm p-6 space-y-5">
        <h2 className="font-semibold text-[#1e3a5f]">Your Signature</h2>

        {isCompany && (
          <div className="flex items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Building2 className="h-4 w-4 text-blue-600 shrink-0" />
            <span className="text-blue-800">
              Signing on behalf of <strong>{participant.entity_name}</strong>
              {participant.signer_role && <> · {participant.signer_role}</>}
            </span>
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="signer-name">Full Name</Label>
          <Input
            id="signer-name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Full Name (e.g. John Smith)"
            maxLength={120}
          />
          {signerName.trim() && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <PenLine className="h-3 w-3" />
              Your cursive signature will be rendered as: <em className="ml-1 text-[#1e3a5f]">{signerName}</em>
            </p>
          )}
        </div>

        {isCompany && (
          <div className="flex items-start gap-3">
            <Checkbox
              id="authorised-company"
              checked={authorisedCompany}
              onCheckedChange={setAuthorisedCompany}
            />
            <Label htmlFor="authorised-company" className="text-sm leading-relaxed cursor-pointer">
              I confirm I am duly authorised to sign this document on behalf of{' '}
              <strong>{participant.entity_name}</strong>
              {participant.signer_role && <> in my capacity as <strong>{participant.signer_role}</strong></>}.
            </Label>
          </div>
        )}

        <div className="flex items-start gap-3">
          <Checkbox
            id="consent"
            checked={consented}
            onCheckedChange={setConsented}
          />
          <Label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
            I confirm I have read and agree to this <strong>{doc.title}</strong> and
            understand this constitutes a legally binding agreement.
            I am authorised to sign where applicable and am doing so of my own free will.
          </Label>
        </div>

        <Button
          className="w-full bg-[#1e3a5f] hover:bg-[#163050]"
          onClick={handleSign}
          disabled={submitting || !documentAccessible || !documentViewed || !consented || !signerName.trim() || (isCompany && !authorisedCompany)}
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <PenLine className="h-4 w-4 mr-2" />
          )}
          Sign document
        </Button>

        {documentAccessible && !documentViewed && (
          <p className="text-xs text-amber-700 text-center">
            Please review the document above before signing
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Your signature, IP address, and timestamp will be recorded for audit purposes
          in accordance with SIYAK's privacy policy.
        </p>
      </div>
    </div>
  );
}
